import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Text, Badge } from '@components/base/display';
import { Table } from '@components/base/data';
import { HStack, VStack } from '@components/base/layout';
import { Alert, Spinner } from '@components/base/feedback';
import { Select, TextField, DateRangePicker } from '@components/base/inputs';
import type { DateRange } from '@components/base/inputs';
import { Button } from '@components/base/actions';
import { tracesService } from '../../api/tracesService';
import type {
  TraceListItem,
  TraceListParams,
} from '../../api/tracesService';
import { tenantsService } from '../../api/tenantsService';
import type { Tenant } from '../../api/tenantsService';
import { useIsAdminTenant } from '../../auth/useIsAdminTenant';
import { useAdminAuthStore } from '../../auth/store';

type Preset = '15m' | '1h' | '4h' | '24h' | '7d' | 'custom';

const PRESET_OPTIONS = [
  { value: '15m', label: 'Last 15 minutes' },
  { value: '1h', label: 'Last 1 hour' },
  { value: '4h', label: 'Last 4 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: 'custom', label: 'Custom range' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'ok', label: 'OK' },
  { value: 'error', label: 'Error' },
];

const MAX_RANGE_MS = 7 * 24 * 60 * 60 * 1000;

function parseTraceTimestamp(s: string): Date {
  const t = s.replace(' ', 'T');
  const dot = t.indexOf('.');
  const trimmed = dot === -1 ? t : t.slice(0, dot + 4);
  return new Date(trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`);
}

function formatTraceTimestamp(s: string): string {
  return parseTraceTimestamp(s).toLocaleString();
}

function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function traceStatusVariant(
  code: string,
  httpStatus: number | null,
): 'success' | 'error' | 'default' {
  if (code === 'STATUS_CODE_ERROR') return 'error';
  if (httpStatus !== null && httpStatus >= 400) return 'error';
  if (httpStatus !== null && httpStatus >= 200 && httpStatus < 300)
    return 'success';
  if (code === 'STATUS_CODE_OK') return 'success';
  return 'default';
}

function statusShortLabel(code: string): string {
  if (code === 'STATUS_CODE_OK') return 'OK';
  if (code === 'STATUS_CODE_ERROR') return 'ERR';
  return '—';
}

function mapTraceListError(error: unknown): { title: string; body: string } {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401) {
      return {
        title: 'Session expired',
        body: 'Your session has expired. Reload to sign in again.',
      };
    }
    if (status === 403) {
      return {
        title: 'Access denied',
        body: 'Your account does not have the `observability.view` permission. This permission is granted by a global RBAC role (e.g. `admin` or `viewer`) — tenant membership alone is not enough. Ask an administrator to assign you the `admin` or `viewer` role.',
      };
    }
  }
  if (error instanceof Error) {
    return { title: 'Failed to load traces', body: error.message };
  }
  return {
    title: 'Failed to load traces',
    body: 'An unexpected error occurred while loading traces.',
  };
}

function deriveRange(
  preset: Preset,
  customRange: DateRange | null,
  now: Date,
): { from: string; to: string } | null {
  const toIso = now.toISOString();
  switch (preset) {
    case '15m':
      return {
        from: new Date(now.getTime() - 15 * 60_000).toISOString(),
        to: toIso,
      };
    case '1h':
      return {
        from: new Date(now.getTime() - 60 * 60_000).toISOString(),
        to: toIso,
      };
    case '4h':
      return {
        from: new Date(now.getTime() - 4 * 60 * 60_000).toISOString(),
        to: toIso,
      };
    case '24h':
      return {
        from: new Date(now.getTime() - 24 * 60 * 60_000).toISOString(),
        to: toIso,
      };
    case '7d':
      return {
        from: new Date(now.getTime() - 7 * 24 * 60 * 60_000).toISOString(),
        to: toIso,
      };
    case 'custom': {
      if (!customRange?.start || !customRange?.end) return null;
      const start = new Date(customRange.start);
      start.setHours(0, 0, 0, 0);
      const end = new Date(customRange.end);
      end.setHours(23, 59, 59, 999);
      return { from: start.toISOString(), to: end.toISOString() };
    }
  }
}

export function TraceListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdminTenant = useIsAdminTenant();
  const activeTenantId = useAdminAuthStore((s) => s.activeTenantId);

  // ---- Filter state hydrated from URL ----

  const preset = (searchParams.get('preset') as Preset | null) ?? '1h';
  const customFromIso = searchParams.get('from') ?? '';
  const customToIso = searchParams.get('to') ?? '';
  const userId = searchParams.get('user_id') ?? '';
  const tenantIdFilter = searchParams.get('tenant_id') ?? '';
  const status = searchParams.get('status') ?? '';
  const route = searchParams.get('route') ?? '';

  const customRange: DateRange | null = useMemo(() => {
    if (preset !== 'custom') return null;
    return {
      start: customFromIso ? new Date(customFromIso) : null,
      end: customToIso ? new Date(customToIso) : null,
    };
  }, [preset, customFromIso, customToIso]);

  // ---- Stable "now" so the derived range does not refetch every render ----

  const [nowAnchor, setNowAnchor] = useState(() => new Date());

  const range = useMemo(
    () => deriveRange(preset, customRange, nowAnchor),
    [preset, customRange, nowAnchor],
  );

  const rangeError = useMemo(() => {
    if (!range) return null;
    const diff = Date.parse(range.to) - Date.parse(range.from);
    if (diff > MAX_RANGE_MS) return 'too-large';
    if (diff <= 0) return 'invalid';
    return null;
  }, [range]);

  // ---- Pagination ----

  const [pages, setPages] = useState<TraceListItem[][]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  // Reset pages whenever filters change. Skip first run so the mount does
  // not invalidate the initial `nowAnchor` and trigger a duplicate fetch.
  const filterKey = `${preset}|${customFromIso}|${customToIso}|${userId}|${tenantIdFilter}|${status}|${route}`;
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return;
    }
    setPages([]);
    setCursor(undefined);
    setNowAnchor(new Date());
  }, [filterKey]);

  // ---- Tenant options for super-admin ----

  const tenantsQuery = useQuery({
    queryKey: ['admin', 'tenants', 'all'],
    queryFn: () => tenantsService.list(),
    enabled: isAdminTenant,
  });

  const tenantOptions = useMemo(() => {
    const list: { value: string; label: string }[] = [
      { value: '', label: 'All tenants' },
    ];
    const data = tenantsQuery.data?.data as Tenant[] | undefined;
    if (data) {
      for (const t of data) {
        list.push({ value: String(t.id), label: t.name });
      }
    }
    return list;
  }, [tenantsQuery.data]);

  // ---- Main query ----

  const queryEnabled = Boolean(range && !rangeError);

  const queryParams: TraceListParams | null = range
    ? {
        from: range.from,
        to: range.to,
        user_id: userId || undefined,
        tenant_id:
          isAdminTenant && tenantIdFilter ? tenantIdFilter : undefined,
        status: (status as 'ok' | 'error') || undefined,
        route: route || undefined,
        per_page: 50,
        cursor,
      }
    : null;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['admin', 'observability', 'traces', queryParams],
    queryFn: () => {
      if (!queryParams) {
        return Promise.reject(new Error('Missing range'));
      }
      return tracesService.list(queryParams);
    },
    enabled: queryEnabled,
  });

  // Append fetched page to local pages buffer when cursor changes.
  useEffect(() => {
    if (!data) return;
    setPages((prev) => {
      // If this is a fresh first page (cursor undefined), replace.
      if (cursor === undefined) return [data.data];
      // Otherwise append.
      return [...prev, data.data];
    });
  }, [data, cursor]);

  // ---- Filter handlers ----

  const updateParam = (key: string, value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const handlePresetChange = (val: string | string[]): void => {
    const value = Array.isArray(val) ? (val[0] ?? '1h') : val;
    const next = new URLSearchParams(searchParams);
    next.set('preset', value);
    if (value !== 'custom') {
      next.delete('from');
      next.delete('to');
    }
    setSearchParams(next);
  };

  const handleCustomRangeChange = (newRange: DateRange): void => {
    const next = new URLSearchParams(searchParams);
    if (newRange.start) next.set('from', newRange.start.toISOString());
    else next.delete('from');
    if (newRange.end) next.set('to', newRange.end.toISOString());
    else next.delete('to');
    setSearchParams(next);
  };

  const handleLoadMore = (): void => {
    const nextCursor = data?.meta?.next_cursor;
    if (typeof nextCursor === 'string' && nextCursor.length > 0) {
      setCursor(nextCursor);
    }
  };

  // ---- Derived state ----

  const items = pages.flat();
  const nextCursor = data?.meta?.next_cursor;
  const showLoadMore =
    typeof nextCursor === 'string' && nextCursor.length > 0;

  // ---- Initial loading ----

  const showInitialSpinner = isLoading && items.length === 0 && queryEnabled;

  return (
    <VStack gap={6}>
      {/* Page header */}
      <VStack gap={1}>
        <Text as="h1" size="2xl" weight="bold">
          Traces
        </Text>
        <Text size="sm" color="muted">
          Inspect distributed traces across services
        </Text>
      </VStack>

      {/* Filters */}
      <HStack gap={4} wrap>
        <Select
          label="Time range"
          options={PRESET_OPTIONS}
          value={preset}
          onChange={handlePresetChange}
          size="sm"
        />
        {preset === 'custom' && (
          <DateRangePicker
            label="Custom range"
            value={customRange ?? { start: null, end: null }}
            onChange={handleCustomRangeChange}
            size="sm"
          />
        )}
        {isAdminTenant ? (
          <Select
            label="Tenant"
            options={tenantOptions}
            value={tenantIdFilter}
            onChange={(val) =>
              updateParam(
                'tenant_id',
                Array.isArray(val) ? (val[0] ?? '') : val,
              )
            }
            size="sm"
          />
        ) : (
          <TextField
            label="Tenant"
            value={activeTenantId ?? ''}
            disabled
            size="sm"
          />
        )}
        <TextField
          label="User ID"
          value={userId}
          onChange={(e) => updateParam('user_id', e.target.value)}
          size="sm"
        />
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={status}
          onChange={(val) =>
            updateParam('status', Array.isArray(val) ? (val[0] ?? '') : val)
          }
          size="sm"
        />
        <TextField
          label="Route"
          value={route}
          onChange={(e) => updateParam('route', e.target.value)}
          size="sm"
        />
      </HStack>

      {rangeError === 'too-large' && (
        <Alert variant="error" title="Time range too large">
          The selected range exceeds the 7-day maximum. Please narrow it.
        </Alert>
      )}

      {/* Loading / error / empty / table */}
      {showInitialSpinner ? (
        <VStack align="center" justify="center" gap={2} className="py-16">
          <Spinner size="lg" />
          <Text color="muted">Loading traces...</Text>
        </VStack>
      ) : error ? (
        (() => {
          const mapped = mapTraceListError(error);
          return (
            <Alert variant="error" title={mapped.title}>
              {mapped.body}
            </Alert>
          );
        })()
      ) : items.length === 0 && !isFetching ? (
        <Alert variant="info" title="No traces found">
          No traces match the current filters.
        </Alert>
      ) : (
        <>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Time</Table.HeaderCell>
                <Table.HeaderCell align="center">Status</Table.HeaderCell>
                <Table.HeaderCell>Route</Table.HeaderCell>
                <Table.HeaderCell>User</Table.HeaderCell>
                <Table.HeaderCell>Tenant</Table.HeaderCell>
                <Table.HeaderCell align="right">Duration</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {items.map((trace) => (
                <Table.Row
                  key={trace.trace_id}
                  onClick={() =>
                    navigate(
                      `/admin/observability/traces/${trace.trace_id}`,
                    )
                  }
                >
                  <Table.Cell>
                    <Text size="sm" color="muted">
                      {formatTraceTimestamp(trace.timestamp)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell align="center">
                    <Badge
                      variant={traceStatusVariant(
                        trace.status_code,
                        trace.http_status,
                      )}
                      size="sm"
                    >
                      {trace.http_status !== null
                        ? String(trace.http_status)
                        : statusShortLabel(trace.status_code)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <VStack gap={0}>
                      <Text weight="medium">
                        {trace.http_method
                          ? `${trace.http_method} ${trace.http_route ?? '—'}`
                          : (trace.span_name ?? '—')}
                      </Text>
                      {trace.url_path && (
                        <Text size="sm" color="muted">
                          {trace.url_path}
                        </Text>
                      )}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="sm">{trace.user_id ?? '—'}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="sm">{trace.tenant_id ?? '—'}</Text>
                  </Table.Cell>
                  <Table.Cell align="right">
                    <Text size="sm">{formatDurationMs(trace.duration_ms)}</Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {showLoadMore && (
            <HStack justify="center" className="py-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleLoadMore}
                disabled={isFetching}
              >
                {isFetching ? 'Loading...' : 'Load more'}
              </Button>
            </HStack>
          )}
        </>
      )}
    </VStack>
  );
}
